import type { Agent } from '@/features/agents/types/agent.types'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { cn } from '@/shared/lib/utils'

interface AgentSelectProps {
  value: string
  onValueChange: (value: string) => void
  agents: Agent[]
  className?: string
  placeholder?: string
}

export function AgentSelect({
  value,
  onValueChange,
  agents,
  className,
  placeholder = 'Select an agent',
}: AgentSelectProps) {
  const selectedAgent = agents.find(agent => agent.id === value)

  const getSelectedDisplay = () => {
    if (!selectedAgent) return placeholder

    return selectedAgent.name
  }

  const getSignalCounts = (agent: Agent) => {
    const taskSignals = agent.taskSignalsCount ?? 0
    const workflowSignals = agent.workflowSignalsCount ?? 0
    const outcomeSignals = agent.outcomeSignalsCount ?? 0
    const totalSignals = agent.totalSignalsCount ?? 0

    return { taskSignals, workflowSignals, outcomeSignals, totalSignals }
  }

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className={cn('mt-3', className)}>
        <SelectValue>{value ? getSelectedDisplay() : placeholder}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {agents.length === 0 ? (
          <div className='p-2 text-sm text-muted-foreground'>
            No agents available
          </div>
        ) : (
          agents.map(agent => {
            const counts = getSignalCounts(agent)
            return (
              <SelectItem key={agent.id} value={agent.id}>
                <div className='py-1'>
                  <div className='font-medium'>{agent.name}</div>
                  {agent.description && (
                    <div className='text-xs text-muted-foreground mb-1'>
                      {agent.description}
                    </div>
                  )}
                  <div className='text-xs text-muted-foreground'>
                    {counts.totalSignals} signals • Tasks: {counts.taskSignals}{' '}
                    • Workflows: {counts.workflowSignals} • Outcomes:{' '}
                    {counts.outcomeSignals}
                  </div>
                </div>
              </SelectItem>
            )
          })
        )}
      </SelectContent>
    </Select>
  )
}
